import React from 'react';
import { Typography, IconButton, Icon } from '@material-ui/core';

/**
 * The configured size of an IBM volume is never in just one place. A volume may carry its
 * own capacity override, inherit the storage config's head-node template, or - the common
 * case - carry nothing at all and take the policy default. `size` is left null precisely to
 * mean "whatever the policy says", so reading it alone is what produced the "(nullGB)" chip.
 */
export const resolveVolumeSize = (volume, storageConfig, policy) =>
    volume?.size
    || volume?.overrides?.capacity
    || storageConfig?.headNodeVolumes?.[0]?.overrides?.capacity
    || policy?.ibmParams?.capacity
    || null;

/**
 * IbmVolumeChip - Volume chip for IBM Heidi AI storage volumes.
 *
 * IBM volumes describe themselves with policyId + overrides, not the size/policyName/
 * performanceOverrides/resiliencyConfig shape the generic VolumeChips expects, so they
 * need their own chip rather than the shared one.
 *
 * @param {Object} props
 * @param {Object} props.volume - The volume object
 * @param {Object} props.storageConfig - The storage configuration the volume references
 * @param {Object} props.policy - The resolved storage policy (supplies the default size)
 * @param {Function} props.onEdit - Called when the edit icon is clicked
 * @param {Function} props.onDelete - Called when the delete icon is clicked
 */
const IbmVolumeChip = ({ volume, storageConfig, policy, onEdit, onDelete }) => {
    const storageConfigName = storageConfig?.name || storageConfig?.providerName || 'Unknown Storage';
    const displaySize = resolveVolumeSize(volume, storageConfig, policy);

    return (
        <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: '#E3F2FD',
            borderRadius: '16px',
            padding: '4px 10px',
            height: '26px',
            border: '1px solid #BBDEFB',
            marginRight: '8px',
            marginBottom: '4px',
        }}>
            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '8px' }}>
                <Typography variant="body2" style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#1565C0',
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {displaySize ? `${storageConfigName} (${displaySize}GB)` : storageConfigName}
                </Typography>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); if (onEdit) onEdit(); }}
                    style={{
                        padding: '1px', width: '16px', height: '16px',
                        color: '#1976D2', backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    }}
                >
                    <Icon style={{ fontSize: '11px' }}>edit</Icon>
                </IconButton>
                <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); if (onDelete) onDelete(); }}
                    style={{
                        padding: '1px', width: '16px', height: '16px',
                        color: '#D32F2F', backgroundColor: 'rgba(211, 47, 47, 0.1)',
                    }}
                >
                    <Icon style={{ fontSize: '11px' }}>delete</Icon>
                </IconButton>
            </div>
        </div>
    );
};

export default IbmVolumeChip;
